/**
 * /admin/gateway-models - managed-AI gateway runtime model list (OCP-519,
 * epic OCP-395).
 *
 * The LiteLLM gateway (ai.orboto.io) serves every tenant's AI. Its model list
 * used to live only in an on-disk config file - changing a model meant an SSH
 * edit + restart. This page (and the ocp_*_gateway_model MCP tools) manage the
 * list at runtime: view / add / edit / remove, taking effect live.
 *
 * EU-only enforcement (the load-bearing bit): every add/edit must point its
 * `api_base` at an allowlisted EU provider host. The server rejects anything
 * else with `non_eu_api_base` - this allowlist is what keeps a US/CN endpoint
 * out of the model list now that the DB is runtime-editable (OCP-395 GDPR
 * constraint). The permitted hosts are shown below; extending them is a
 * deliberate code change, not a UI action.
 *
 * OCP-555 added the observe half at the top of the page: GatewayHealthCard
 * surfaces the readiness probe (is the gateway answering, is its auth DB up),
 * so a flap is caught here instead of in a customer's error stream. It fetches
 * independently - a health-read failure must never blank the model list.
 *
 * OCP-882 added GatewayDeploymentProbeCard under it: the last synthetic request
 * per deployment (ok, latency, error, since), because readiness stayed green
 * while one provider stalled every embedding for 66 minutes. The model rows
 * carry each deployment's timeout and retries next to its order.
 *
 * OCP-949 added GatewayFailoverCard ("Provider failover"): OCP drives the order
 * of every model group from those probes; the card shows per group the
 * preferred and the active order, the state, the history, and hold / promote.
 *
 * OCP-558 made PRICES first-class here. orboto is the source of truth for model
 * costs and pushes them into the gateway; a model with no price accrues no
 * gateway-side spend, which silently disarms the per-key budget backstop that
 * limits spend when the metering sweep is not running. Prices are entered and
 * shown per 1M tokens (the unit invoices use), an unpriced model is flagged in
 * its row, and the open operator alert is repeated at the top of the page.
 */
import { useCallback, useEffect, useState } from 'react';

import { GatewayHealthCard } from '../components/GatewayHealthCard';
import { GatewayDeploymentProbeCard } from '../components/GatewayDeploymentProbeCard';
import { GatewayFailoverCard } from '../components/GatewayFailoverCard';
import { GatewayCapacityCard } from '../components/GatewayCapacityCard';
import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';
import { GatewayRouterSettingsCard } from '../components/GatewayRouterSettingsCard';

interface GatewayModel {
  model_id: string;
  model_name: string;
  litellm_model: string | null;
  api_base: string | null;
  provider: string | null;
  db_model: boolean;
  input_cost_per_token: number | null;
  output_cost_per_token: number | null;
  /** USD per 1M tokens - the unit shown + entered here (OCP-558). */
  input_cost_per_mtok: number | null;
  /** OCP-667 - what the customer pays per 1M tokens (EUR microcents + orboto Token); null = no customer price. */
  customer_price?: { in_microcents_per_mtok: number; out_microcents_per_mtok: number; in_orboto_token_per_1m: number | null; out_orboto_token_per_1m: number | null } | null;
  output_cost_per_mtok: number | null;
  /** False when this model has no input price - the backstop-disarming case. */
  priced: boolean;
  /** OCP-398 - deployment priority within the model_name group (null = load-balanced). */
  order?: number | null;
  /** OCP-882 - per-attempt timeout on this deployment in seconds (null = router default). */
  timeout?: number | null;
  /** OCP-882 - retries on this deployment before the next order (null = router default, 0 = fail over at once). */
  num_retries?: number | null;
  /** OCP-882 - drop_params as the gateway holds it (null = unset). */
  drop_params?: boolean | null;
  /** OCP-882 - rpm / tpm as the gateway holds them (null = unset). */
  rpm?: number | null;
  tpm?: number | null;
  /** OCP-614 - tenant-facing catalogue metadata (OCP's own row, kept across edits). */
  capabilities?: string[];
  display_name?: string | null;
  context_length?: number | null;
  reasoning_supported?: boolean | null;
  reasoning_min?: string | null;
  deprecated?: boolean;
}

const CAPABILITIES = ['chat', 'embedding', 'rerank', 'vision'] as const;
const REASONING_FLOORS = ['off', 'low', 'medium', 'high'] as const;

interface AllowlistEntry {
  host: string;
  provider: string;
  note: string;
}

interface PriceAlert {
  alerting: boolean;
  since: string | null;
  last_alerted_at: string | null;
  last_reason: string | null;
  missing_models: string[];
}

interface ModelsResponse {
  items: GatewayModel[];
  gateway_configured: boolean;
  allowlist: AllowlistEntry[];
  unpriced_count: number;
  price_alert: PriceAlert;
}

interface FormState {
  model_name: string;
  litellm_model: string;
  api_base: string;
  api_key: string;
  input_cost_per_mtok: string;
  output_cost_per_mtok: string;
  drop_params: boolean;
  /** OCP-398 - priority within the model_name group ('' = unordered). */
  order: string;
  /** OCP-882 - per-attempt timeout in seconds ('' = router default). */
  timeout: string;
  /** OCP-882 - retries before the next order ('' = router default). */
  num_retries: string;
  /**
   * OCP-882 - the entry being edited carried an explicit drop_params, so the
   * update re-sends it as shown (true or false).
   */
  drop_params_known: boolean;
  /** OCP-882 - rpm / tpm caps ('' = unchanged on edit, unset on add). */
  rpm: string;
  tpm: string;
  /** OCP-614 - catalogue metadata; the edit form loads and re-sends all of it. */
  display_name: string;
  context_length: string;
  capabilities: string[];
  /** 'unknown' | 'yes' | 'no' - a thinking model declaration. */
  reasoning_supported: 'unknown' | 'yes' | 'no';
  reasoning_min: string;
  deprecated: boolean;
}

const EMPTY_FORM: FormState = {
  model_name: '',
  litellm_model: '',
  api_base: '',
  api_key: '',
  input_cost_per_mtok: '',
  output_cost_per_mtok: '',
  drop_params: false,
  order: '',
  timeout: '',
  num_retries: '',
  drop_params_known: false,
  rpm: '',
  tpm: '',
  display_name: '',
  context_length: '',
  capabilities: [],
  reasoning_supported: 'unknown',
  reasoning_min: '',
  deprecated: false,
};

/** Render a USD-per-1M-tokens price, or a dash when it is unset. */
function fmtPrice(perMtok: number | null): string {
  if (perMtok === null) return '-';
  // Sub-cent embedding prices need more than 2 dp to stay readable.
  return `$${perMtok < 0.01 ? perMtok.toFixed(4) : perMtok.toFixed(2)}`;
}

export function AdminGatewayModelsPage() {
  const [data, setData] = useState<ModelsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editing, setEditing] = useState<GatewayModel | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api<ModelsResponse>('/v1/admin/gateway/models');
      setData(res);
    } catch (err) {
      setLoadError(
        err instanceof ApiError ? `Could not load models (HTTP ${err.status}).` : 'Could not load models.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (m: GatewayModel) => {
    setEditing(m);
    setShowForm(true);
  };
  const onSaved = () => {
    setShowForm(false);
    setEditing(null);
    void load();
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">AI gateway models</h1>
          <p className="mt-2 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
            Runtime model list served by the managed AI gateway to every tenant. Changes take effect live
            - no restart. Every model&apos;s endpoint must be an allowlisted EU provider (see below);
            non-EU endpoints are rejected to keep the GDPR EU-only guarantee.
          </p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          disabled={data?.gateway_configured === false}
          className="rounded bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          title={data?.gateway_configured === false ? 'Gateway not configured' : undefined}
        >
          Add model
        </button>
      </div>

      {/* OCP-555 - readiness of the gateway these models are served by. */}
      <GatewayHealthCard />

      {/* OCP-882 - one real request per deployment: a stalled provider behind a healthy gateway. */}
      <GatewayDeploymentProbeCard />

      {/* OCP-949 - OCP drives the order of every model group from those probes. */}
      <GatewayFailoverCard />

      {/* OCP-882 - router-wide failover defaults (retries, timeout, cooldown). */}
      <GatewayRouterSettingsCard />

      {/* OCP-567 - capacity of the gateway HOST: which dimension binds, tenant headroom, scaling action. */}
      <GatewayCapacityCard />

      {/* OCP-558 - a model with no price disarms the per-key spend backstop. */}
      {data && data.gateway_configured && data.unpriced_count > 0 && (
        <div
          role="alert"
          data-testid="unpriced-models-banner"
          className="mt-4 rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300"
        >
          <span className="font-semibold">
            {data.unpriced_count} model{data.unpriced_count === 1 ? '' : 's'} without a price.
          </span>{' '}
          The gateway records no spend for calls through them, so the per-key budget backstop cannot
          stop a runaway workspace while metering is down. Set the cost per 1M tokens on each flagged
          row below.
          {data.price_alert.alerting && data.price_alert.since && (
            <span className="ml-1 opacity-80">
              Operator alerted since {fmtDateTime(data.price_alert.since)}.
            </span>
          )}
        </div>
      )}

      {data && !data.gateway_configured && (
        <div className="mt-4 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          Gateway not configured - managed AI is off fleet-wide. Set{' '}
          <span className="font-mono">AI_GATEWAY_BASE_URL</span> +{' '}
          <span className="font-mono">AI_GATEWAY_MASTER_KEY</span> in the api env, and turn on{' '}
          <span className="font-mono">store_model_in_db</span> on the gateway for edits to persist.
        </div>
      )}

      {/* EU-only allowlist - the enforcement point. */}
      {data && data.allowlist.length > 0 && (
        <section className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-4">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Permitted EU provider hosts (allowlist)
          </h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            A model&apos;s <span className="font-mono">api_base</span> host must match one of these. Adding
            a new provider is a deliberate code change (with an AVV on file), never a runtime add.
          </p>
          <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {data.allowlist.map((a) => (
              <li
                key={a.host}
                className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
              >
                <div className="font-mono text-xs text-gray-800 dark:text-gray-200">{a.host}</div>
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300">{a.provider}</div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{a.note}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {loadError && (
        <div
          role="alert"
          className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {loadError}
        </div>
      )}

      <section className="mt-6">
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : data && data.items.length === 0 ? (
          <p className="text-sm text-gray-500">
            No models in the runtime list yet.
            {data.gateway_configured ? ' Add one, or migrate the config file entries into the DB.' : ''}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">Model name</th>
                  <th className="px-3 py-2">Backend</th>
                  <th className="px-3 py-2">Endpoint</th>
                  <th className="px-3 py-2">Provider</th>
                  <th className="px-3 py-2 text-right" title="Priority within the model group; empty = load-balanced (OCP-398)">Order</th>
                  <th className="px-3 py-2 text-right" title="Per-attempt timeout in seconds / retries on this deployment before the next order; - = router default (OCP-882)">Timeout / retries</th>
                  <th className="px-3 py-2 text-right">In / Mtok</th>
                  <th className="px-3 py-2 text-right">Out / Mtok</th>
                  <th className="px-3 py-2 text-right" title="Customer price per 1M tokens in / out: EUR and orboto Token (ai.model_prices, ai.price_microcents_per_mtok, ai.orboto_token_microcents) - OCP-667">Customer in / out</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {data?.items.map((m) => (
                  <tr key={m.model_id} data-testid="gateway-model-row">
                    <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">{m.model_name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">
                      {m.litellm_model ?? '-'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500 break-all">{m.api_base ?? '-'}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{m.provider ?? '-'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400" data-testid="model-order-cell">{m.order ?? '-'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400" data-testid="model-timeout-cell">
                      {m.timeout != null ? `${m.timeout} s` : '-'} / {m.num_retries ?? '-'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums" data-testid="model-input-price">
                      {m.priced ? (
                        <span className="text-gray-800 dark:text-gray-200">
                          {fmtPrice(m.input_cost_per_mtok)}
                        </span>
                      ) : (
                        <span
                          data-testid="unpriced-badge"
                          title="No input price - the per-key spend backstop cannot bite for this model"
                          className="inline-flex items-center rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300 ring-1 ring-inset ring-amber-300 dark:ring-amber-800"
                        >
                          no price
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {fmtPrice(m.output_cost_per_mtok)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs" data-testid={`customer-price-${m.model_name}`}>
                      {m.customer_price
                        ? `${(m.customer_price.in_microcents_per_mtok / 1_000_000).toFixed(2)} / ${(m.customer_price.out_microcents_per_mtok / 1_000_000).toFixed(2)} EUR${m.customer_price.in_orboto_token_per_1m != null ? ` · ${m.customer_price.in_orboto_token_per_1m} / ${m.customer_price.out_orboto_token_per_1m} Token` : ''}`
                        : <span className="text-gray-400">no customer price</span>}
                    </td>
                    <td className="px-3 py-2">
                      {m.db_model ? (
                        <span className="inline-flex items-center rounded bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-200 dark:ring-green-800">
                          runtime
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
                          file seed
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(m)}
                          className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          Edit
                        </button>
                        <DeleteButton model={m} onDeleted={load} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showForm && data && (
        <ModelForm
          editing={editing}
          allowlist={data.allowlist}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSaved={onSaved}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function DeleteButton({ model, onDeleted }: { model: GatewayModel; onDeleted: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const remove = useCallback(async () => {
    setBusy(true);
    try {
      await api(`/v1/admin/gateway/models/${encodeURIComponent(model.model_id)}`, { method: 'DELETE' });
      onDeleted();
    } finally {
      setBusy(false);
    }
  }, [model.model_id, onDeleted]);

  if (!confirm) {
    return (
      <button
        type="button"
        onClick={() => setConfirm(true)}
        className="rounded border border-red-300 dark:border-red-700 px-2.5 py-1 text-xs font-semibold text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
      >
        Remove
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => void remove()}
        disabled={busy}
        className="rounded bg-red-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
      >
        {busy ? '…' : 'Confirm'}
      </button>
      <button
        type="button"
        onClick={() => setConfirm(false)}
        disabled={busy}
        className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs font-semibold text-gray-600 dark:text-gray-300"
      >
        Cancel
      </button>
    </span>
  );
}

function ModelForm({
  editing,
  allowlist,
  onCancel,
  onSaved,
  onChanged,
}: {
  editing: GatewayModel | null;
  allowlist: AllowlistEntry[];
  onCancel: () => void;
  onSaved: () => void;
  /** The list changed while the form stays open (a reset whose old row is still there). */
  onChanged?: () => void;
}) {
  const [form, setForm] = useState<FormState>(
    editing
      ? {
          model_name: editing.model_name,
          litellm_model: editing.litellm_model ?? '',
          api_base: editing.api_base ?? '',
          api_key: '',
          input_cost_per_mtok:
            editing.input_cost_per_mtok != null ? String(editing.input_cost_per_mtok) : '',
          output_cost_per_mtok:
            editing.output_cost_per_mtok != null ? String(editing.output_cost_per_mtok) : '',
          drop_params: editing.drop_params === true,
          order: editing.order != null ? String(editing.order) : '',
          timeout: editing.timeout != null ? String(editing.timeout) : '',
          num_retries: editing.num_retries != null ? String(editing.num_retries) : '',
          drop_params_known: editing.drop_params != null,
          rpm: editing.rpm != null ? String(editing.rpm) : '',
          tpm: editing.tpm != null ? String(editing.tpm) : '',
          display_name: editing.display_name ?? '',
          context_length: editing.context_length != null ? String(editing.context_length) : '',
          capabilities: editing.capabilities ?? [],
          reasoning_supported: editing.reasoning_supported === true ? 'yes' : editing.reasoning_supported === false ? 'no' : 'unknown',
          reasoning_min: editing.reasoning_min ?? '',
          deprecated: editing.deprecated === true,
        }
      : EMPTY_FORM,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    const body: Record<string, unknown> = {
      model_name: form.model_name.trim(),
      litellm_model: form.litellm_model.trim(),
      api_base: form.api_base.trim(),
    };
    if (form.api_key.trim()) body.api_key = form.api_key.trim();
    // OCP-558 - prices go over the wire per 1M tokens; the api converts to the
    // gateway's per-token unit.
    //
    // OCP-882 - an update is a MERGE (LiteLLM v1.92.0 /model/update keeps every
    // litellm_params key the request leaves out; the catalogue metadata is OCP's
    // own row and keeps what the body omits). An emptied gateway field therefore
    // means "unchanged", not "cleared": to take timeout / retries / order / caps
    // off a deployment use "Remove values" below. The catalogue fields are all
    // loaded into this form and re-sent as shown, null where the operator
    // emptied a nullable one, so what the form shows is what is stored.
    if (form.input_cost_per_mtok.trim()) body.input_cost_per_mtok = Number(form.input_cost_per_mtok);
    if (form.output_cost_per_mtok.trim()) body.output_cost_per_mtok = Number(form.output_cost_per_mtok);
    // An explicit drop_params on the edited entry is re-sent as shown (false is
    // what lets an embedding entry pass `dimensions` through), a new entry
    // sends it only when ticked.
    if (form.drop_params || form.drop_params_known) body.drop_params = form.drop_params;
    // OCP-949 - `order` goes over the wire only when the operator changed it. During a failover the order shown here is the
    // swapped LIVE order; re-sending it on an unrelated edit (a timeout) would make the server treat it as a manual order edit.
    const orderText = form.order.trim();
    const initialOrder = editing?.order != null ? String(editing.order) : '';
    if (orderText && (!editing || orderText !== initialOrder)) body.order = Number(orderText);
    if (form.timeout.trim()) body.timeout = Number(form.timeout);
    if (form.num_retries.trim()) body.num_retries = Number(form.num_retries);
    if (form.rpm.trim()) body.rpm = Number(form.rpm);
    if (form.tpm.trim()) body.tpm = Number(form.tpm);
    const emptyOr = <T,>(v: T | null): T | null | undefined => (v !== null ? v : editing ? null : undefined);
    const display = form.display_name.trim();
    const ctx = form.context_length.trim();
    const dn = emptyOr(display ? display : null);
    if (dn !== undefined) body.display_name = dn;
    const cl = emptyOr(ctx ? Number(ctx) : null);
    if (cl !== undefined) body.context_length = cl;
    if (form.capabilities.length > 0) body.capabilities = form.capabilities;
    const rs = emptyOr(form.reasoning_supported === 'yes' ? true : form.reasoning_supported === 'no' ? false : null);
    if (rs !== undefined) body.reasoning_supported = rs;
    const rm = emptyOr(form.reasoning_supported === 'yes' && form.reasoning_min ? form.reasoning_min : null);
    if (rm !== undefined) body.reasoning_min = rm;
    body.deprecated = form.deprecated;

    try {
      if (editing) {
        await api(`/v1/admin/gateway/models/${encodeURIComponent(editing.model_id)}`, {
          method: 'PATCH',
          body,
        });
      } else {
        await api('/v1/admin/gateway/models', { method: 'POST', body });
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        const b = err.body as { error?: string; reason?: string; host?: string } | null;
        if (b?.error === 'non_eu_api_base') {
          setError(
            `Endpoint rejected: "${b.host ?? form.api_base}" is not an allowlisted EU provider host. ` +
              'Pick one of the permitted hosts, or add the provider in code (with an AVV) first.',
          );
        } else if (b?.error === 'conflicting_cost_fields') {
          setError('Price sent in two different units at once - use the per-1M-token fields only.');
        } else if (b?.error === 'model_name_immutable') {
          setError('The model name cannot change on an existing entry (LiteLLM ignores it). Add the model under the new name and delete this one.');
        } else if (b?.error === 'reasoning_min_requires_reasoning_supported') {
          setError('A reasoning floor needs "Thinking model" set to yes.');
        } else if (b?.error === 'gateway_unconfigured') {
          setError('The gateway is not configured - set AI_GATEWAY_* first.');
        } else if (err.status === 400) {
          setError('Validation failed - check the fields.');
        } else {
          setError(`Save failed (HTTP ${err.status}).`);
        }
      } else {
        setError('Save failed.');
      }
    } finally {
      setBusy(false);
    }
  }, [editing, form, onSaved]);

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="mt-16 w-full max-w-lg rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {editing ? 'Edit model' : 'Add model'}
        </h2>

        <div className="mt-4 space-y-3">
          <Field label="Model name (public)" hint="What tenants request at /v1/models">
            <input
              value={form.model_name}
              onChange={(e) => set('model_name', e.target.value)}
              placeholder="Qwen/Qwen3-Embedding-8B"
              disabled={editing !== null}
              title={editing ? 'The public name cannot change on an existing entry' : undefined}
              className={inputCls}
            />
          </Field>
          <Field label="Backend model (litellm_params.model)" hint="Provider-prefixed routing target">
            <input
              value={form.litellm_model}
              onChange={(e) => set('litellm_model', e.target.value)}
              placeholder="hosted_vllm/Qwen/Qwen3-Embedding-8B"
              className={inputCls}
            />
          </Field>
          <Field label="API base (EU providers only)" hint="Must be an allowlisted host">
            <input
              value={form.api_base}
              onChange={(e) => set('api_base', e.target.value)}
              list="eu-allowlist-hosts"
              placeholder="https://vllm.example.com"
              className={inputCls}
            />
            <datalist id="eu-allowlist-hosts">
              {allowlist.map((a) => (
                <option key={a.host} value={`https://${a.host}`} />
              ))}
            </datalist>
          </Field>
          <Field label="API key reference (optional)" hint="Prefer an env reference, e.g. os.environ/IONOS_API_KEY">
            <input
              value={form.api_key}
              onChange={(e) => set('api_key', e.target.value)}
              placeholder="os.environ/IONOS_API_KEY"
              className={inputCls}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Input cost / 1M tokens" hint="USD - always set this">
              <input
                value={form.input_cost_per_mtok}
                onChange={(e) => set('input_cost_per_mtok', e.target.value)}
                placeholder="0.10"
                inputMode="decimal"
                className={inputCls}
              />
            </Field>
            <Field label="Output cost / 1M tokens" hint="USD, optional for embeddings">
              <input
                value={form.output_cost_per_mtok}
                onChange={(e) => set('output_cost_per_mtok', e.target.value)}
                placeholder="0.60"
                inputMode="decimal"
                className={inputCls}
              />
            </Field>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The input price is what lets the gateway track spend per key. A model without one leaves the
            per-key budget backstop unable to stop a runaway workspace while metering is down - you will
            get an operator alert until it is set. On an existing entry an empty field keeps the stored
            price; enter 0 to mark a model unpriced.
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.drop_params}
              onChange={(e) => set('drop_params', e.target.checked)}
            />
            drop_params (leave off for embeddings so the dimensions param passes through)
          </label>
          <Field label="Priority in its model group (order)">
            <input
              value={form.order}
              onChange={(e) => set('order', e.target.value)}
              placeholder="1 = primary, 2 = failover; empty = load-balanced"
              inputMode="numeric"
              className={inputCls}
              data-testid="model-order"
            />
          </Field>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Deployments that share one model name are load-balanced unless they carry an order. The
            cheapest provider gets 1 and the more expensive one 2; on a price tie the current provider
            stays 1. An embedding fallback must serve the same model weights (same vectors). The order set
            here is also this deployment&apos;s preferred order: automatic failover returns to it after an
            outage (Provider failover card). An unchanged order is not an edit, so saving other fields leaves
            the preferred order alone. To switch the active primary without changing the preferred order,
            use Promote on the Provider failover card.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Timeout (seconds)" hint="per attempt">
              <input
                value={form.timeout}
                onChange={(e) => set('timeout', e.target.value)}
                placeholder="20 (embedding) / 120 (chat)"
                inputMode="decimal"
                className={inputCls}
                data-testid="model-timeout"
              />
            </Field>
            <Field label="Retries" hint="before the next order">
              <input
                value={form.num_retries}
                onChange={(e) => set('num_retries', e.target.value)}
                placeholder="0 = fail over at once"
                inputMode="numeric"
                className={inputCls}
                data-testid="model-num-retries"
              />
            </Field>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Empty on a new entry means the router-wide default (see Router failover settings), which
            lets a stalled provider hang calls instead of failing over. On an existing entry an empty
            field keeps the stored value (the gateway merges updates); remove a value with
            &quot;Remove values&quot; below. Live values: embedding 20 s with 2 retries on every order (an instant
            ReadTimeout is free to retry); chat 120 s with 0 retries on order 1, 1 retry on the last order and on a
            single backend.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Requests / minute cap" hint="rpm">
              <input value={form.rpm} onChange={(e) => set('rpm', e.target.value)} inputMode="numeric" className={inputCls} data-testid="model-rpm" />
            </Field>
            <Field label="Tokens / minute cap" hint="tpm">
              <input value={form.tpm} onChange={(e) => set('tpm', e.target.value)} inputMode="numeric" className={inputCls} data-testid="model-tpm" />
            </Field>
          </div>

          <h3 className="pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Catalogue (shown to tenants)</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Display name">
              <input value={form.display_name} onChange={(e) => set('display_name', e.target.value)} className={inputCls} data-testid="model-display-name" />
            </Field>
            <Field label="Context length" hint="tokens">
              <input value={form.context_length} onChange={(e) => set('context_length', e.target.value)} inputMode="numeric" className={inputCls} data-testid="model-context-length" />
            </Field>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-gray-700 dark:text-gray-300" data-testid="model-capabilities">
            {CAPABILITIES.map((c) => (
              <label key={c} className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={form.capabilities.includes(c)}
                  onChange={(e) => set('capabilities', e.target.checked ? [...form.capabilities, c] : form.capabilities.filter((x) => x !== c))}
                />
                {c}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Thinking model">
              <select value={form.reasoning_supported} onChange={(e) => set('reasoning_supported', e.target.value as FormState['reasoning_supported'])} className={inputCls} data-testid="model-reasoning-supported">
                <option value="unknown">not declared</option>
                <option value="yes">yes</option>
                <option value="no">no</option>
              </select>
            </Field>
            <Field label="Lowest reasoning effort">
              <select value={form.reasoning_min} onChange={(e) => set('reasoning_min', e.target.value)} disabled={form.reasoning_supported !== 'yes'} className={inputCls} data-testid="model-reasoning-min">
                <option value="">no floor</option>
                {REASONING_FLOORS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={form.deprecated} onChange={(e) => set('deprecated', e.target.checked)} />
            deprecated (hidden from recommendations, still callable)
          </label>
          {editing && <ResetValues model={editing} onDone={onSaved} onChanged={onChanged} />}
        </div>

        {error && (
          <div
            role="alert"
            className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded border border-gray-300 dark:border-gray-700 px-4 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !form.model_name.trim() || !form.litellm_model.trim() || !form.api_base.trim() || (editing !== null && form.capabilities.length === 0)}
            className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Add model'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * OCP-882 - take timeout / retries / order / rpm / tpm OFF a deployment. LiteLLM
 * cannot unset a litellm_params key, so the deployment is re-created (new model
 * id, same prices and catalogue). The api key is required because the gateway
 * masks it on read.
 */
function ResetValues({ model, onDone, onChanged }: { model: GatewayModel; onDone: () => void; onChanged?: () => void }) {
  const present: Array<{ key: 'timeout' | 'num_retries' | 'order' | 'rpm' | 'tpm'; label: string }> = [];
  if (model.timeout != null) present.push({ key: 'timeout', label: `timeout (${model.timeout} s)` });
  if (model.num_retries != null) present.push({ key: 'num_retries', label: `retries (${model.num_retries})` });
  if (model.order != null) present.push({ key: 'order', label: `order (${model.order})` });
  if (model.rpm != null) present.push({ key: 'rpm', label: `requests/min (${model.rpm})` });
  if (model.tpm != null) present.push({ key: 'tpm', label: `tokens/min (${model.tpm})` });
  const [picked, setPicked] = useState<string[]>([]);
  const [apiKey, setApiKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set when the replacement was added but the old row could not be deleted: both exist. */
  const [leftover, setLeftover] = useState<{ oldId: string; newId: string } | null>(null);
  if (present.length === 0 && !leftover) return null;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ model?: { model_id?: string }; replaced_model_id?: string; old_deleted?: boolean }>(
        `/v1/admin/gateway/models/${encodeURIComponent(model.model_id)}/reset`,
        { method: 'POST', body: { fields: picked, api_key: apiKey.trim() } },
      );
      if (res.old_deleted === false) {
        // Two identical deployments now share the group and traffic is split between them.
        setLeftover({ oldId: res.replaced_model_id ?? model.model_id, newId: res.model?.model_id ?? '' });
        onChanged?.();
      } else {
        onDone();
      }
    } catch (err) {
      const b = err instanceof ApiError ? (err.body as { error?: string; params?: string[] } | null) : null;
      setError(
        b?.error === 'masked_params'
          ? `Cannot reset: the gateway only shows ${(b.params ?? []).join(', ')} masked, so it cannot be copied. Re-add the deployment by hand.`
          : err instanceof ApiError
            ? `Reset failed (HTTP ${err.status}).`
            : 'Reset failed.',
      );
    } finally {
      setBusy(false);
    }
  };

  const deleteOld = async () => {
    if (!leftover) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/admin/gateway/models/${encodeURIComponent(leftover.oldId)}`, { method: 'DELETE' });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? `Delete failed (HTTP ${err.status}).` : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  if (leftover) {
    return (
      <div role="alert" data-testid="reset-leftover" className="rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-3 text-sm text-amber-800 dark:text-amber-300">
        <p className="font-semibold">The replacement was added, but the old entry could not be deleted.</p>
        <p className="mt-1">
          Two identical deployments now serve this model and traffic is split between them. Delete the old entry{' '}
          <span className="font-mono">{leftover.oldId}</span> (the new one is <span className="font-mono">{leftover.newId || 'unknown'}</span>).
        </p>
        {error && <div className="mt-2 text-red-700 dark:text-red-300">{error}</div>}
        <button
          type="button"
          onClick={() => void deleteOld()}
          disabled={busy}
          className="mt-2 rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
        >
          {busy ? 'Deleting…' : 'Delete old entry'}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="reset-values" className="rounded border border-gray-200 dark:border-gray-700 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Remove values (back to the router default)</h3>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        The gateway cannot unset a value in place, so the deployment is re-created without the ticked ones: the new entry is
        added first, then this one is deleted. It gets a new model id; prices, catalogue and every other setting carry over.
        The key reference is required because the gateway hides keys.
      </p>
      <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-700 dark:text-gray-300">
        {present.map((f) => (
          <label key={f.key} className="flex items-center gap-1.5">
            <input type="checkbox" checked={picked.includes(f.key)} onChange={(e) => setPicked(e.target.checked ? [...picked, f.key] : picked.filter((x) => x !== f.key))} />
            {f.label}
          </label>
        ))}
      </div>
      <input
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        placeholder="os.environ/SCALEWAY_API_KEY (the entry's key reference)"
        className={`${inputCls} mt-2`}
        data-testid="reset-api-key"
      />
      {error && <div role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>}
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy || picked.length === 0 || !apiKey.trim()}
        className="mt-2 rounded border border-red-300 dark:border-red-700 px-3 py-1.5 text-xs font-semibold text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? 'Re-creating…' : 'Remove ticked values'}
      </button>
    </div>
  );
}

const inputCls =
  'w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
      {hint && <span className="ml-2 text-xs text-gray-400">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
